import axios, { type AxiosProgressEvent } from 'axios';
import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { xhr } from '@shared/config';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import { useVideoLoader } from '@shared/hooks/useVideoLoader.ts';
import type { PromoVideo } from '@shared/types/promo-videos';
import { promoApi, usePromo } from '@src/entities/promo-video';
import { base64ToFile } from '@src/library/helpers/events/base64ToFile';
import type { PromoFormValues } from '../lib';

interface PromoCreateRes {
  id: number;
  progress_bar_url: string;
  upload_url: string;
}

export const usePromoUpload = (
  videoId: number,
  onCancel?: () => void,
  onSuccess?: (id: number) => void
) => {
  const { module } = useModuleByCodeParam();
  const [progress, setProgress] = useState<AxiosProgressEvent>();
  const isCanceled = useRef(false);
  const [uploadStatus, setUploadStatus] = React.useState<'idle' | 'loading'>('idle');
  const { setPromo, resetPromo } = usePromo(videoId);

  const instance = axios.create({
    withCredentials: false,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      setProgress(progressEvent);
    },
  });

  const create = async (data: PromoFormValues) => {
    setUploadStatus('loading');
    try {
      const promoData = new FormData();
      const promoConfig = { params: { module: module?.id } };

      if (data.screenshot_url) {
        if (typeof data.screenshot_url === 'string') {
          promoData.append('preview', base64ToFile(data.screenshot_url));
        } else if (data.screenshot_url instanceof File) {
          promoData.append('preview', data.screenshot_url);
        }
      }
      promoData.append('video_type', data.video_type);
      if (data.url) {
        promoData.append('embed_url', data.url);
      }

      const res = await xhr.post<PromoCreateRes>(promoApi, promoData, promoConfig);
      if (!res.data) throw res;

      const formData = new FormData();

      if (!data.url) {
        if (data.file) {
          formData.append('file', data.file);
        }

        await instance.post(res.data.upload_url, formData);
        const checkRes = await xhr.get<PromoVideo>(`${promoApi}${res.data.id}/check/`);
        if (!checkRes.data) throw checkRes;
        setPromo(checkRes.data);
      } else {
        const promoPatchRes = await xhr.patch(`${promoApi}/${res.data.id}/`, promoData);
        if (!promoPatchRes.data) throw promoPatchRes;
        setPromo({ ...promoPatchRes.data });
      }

      setUploadStatus('idle');
      // localStorage.setItem('promoId', res.data.id.toString());

      // if (isCanceled.current) {
      //   console.log('canceled');
      //   onCancel && onCancel();
      // } else {
      //   console.log('success');
      !isCanceled.current && onSuccess && onSuccess(res.data.id);
      // }

      // if (onSuccess) onSuccess(res.data.id);
    } catch (error) {
      console.error(error);
    } finally {
      setUploadStatus('idle');
    }
  };

  const update = async (data: PromoFormValues) => {
    setUploadStatus('loading');
    resetPromo();
    try {
      const promoData = new FormData();

      if (data.screenshot_url) {
        if (typeof data.screenshot_url === 'string' && data.screenshot_url.includes('base64')) {
          promoData.append('preview', base64ToFile(data.screenshot_url));
        } else if (data.screenshot_url instanceof File) {
          promoData.append('preview', data.screenshot_url);
        }
      }
      promoData.append('video_type', data.video_type);
      // data.url && promoData.append('embed_url', data.url);
      const res = await xhr.patch(`${promoApi}${videoId}/`, promoData);
      if (!res.data) throw res;

      if (!data.url) {
        const formData = new FormData();
        if (data.file) {
          formData.append('file', data.file);
        }

        await instance.post(res.data.upload_url, formData);
        const checkRes = await xhr.get<PromoVideo>(`${promoApi}${res.data.id}/check/`);
        if (!checkRes.data) throw checkRes;
        setPromo(checkRes.data);
      } else {
        const formData = new FormData();
        formData.append('embed_url', data.url);
        const promoPatchRes = await xhr.patch(`${promoApi}/${res.data.id}/`, formData);
        if (!promoPatchRes.data) throw promoPatchRes;
        setPromo({ ...promoPatchRes.data });
      }

      setUploadStatus('idle');
      // localStorage.setItem('promoId', res.data.id.toString());
      onSuccess && onSuccess(res.data.id);
    } catch (error) {
      console.error(error);
    } finally {
      setUploadStatus('idle');
    }
  };

  const onSubmit = (data: PromoFormValues) => {
    // console.log("videoId;",videoId );
    isNaN(videoId) ? create(data) : update(data);
  };
  return {
    onSubmit,
    progress,
    uploadStatus,
    doAbortLoading: () => {
      isCanceled.current = true;
      onCancel && onCancel();
    },
  };
};

export const usePromoUploadNew = (
  videoId: number,
  onCancel?: () => void,
  onSuccess?: (id: number) => void
) => {
  const { code } = useParams<{ code: string }>();
  const { module } = useModule(code);
  // const [progress, setProgress] = useState<AxiosProgressEvent>();
  // const [uploadStatus, setUploadStatus] = React.useState<'idle' | 'loading'>('idle');
  const { setPromo, resetPromo } = usePromo(videoId);
  const {
    uploadStatus,
    progress,
    create: createVideo,
  } = useVideoLoader({ api: promoApi, onSuccess: setPromo });

  // const instance = axios.create({
  //   withCredentials: false,
  //   headers: { 'Content-Type': 'multipart/form-data' },
  //   onUploadProgress: (progressEvent: AxiosProgressEvent) => {
  //     setProgress(progressEvent);
  //   },
  // });

  const create = async (data: PromoFormValues) => {
    // setUploadStatus('loading');
    try {
      const additionalDataToPost = new FormData();
      const config = { params: { module: module?.id } };

      if (data.screenshot_url) {
        if (typeof data.screenshot_url === 'string') {
          additionalDataToPost.append('preview', base64ToFile(data.screenshot_url));
        } else if (data.screenshot_url instanceof File) {
          additionalDataToPost.append('preview', data.screenshot_url);
        }
      }
      // const {}
      // if (data.youtube_url) {
      //   promoData.append('youtube_url', data.youtube_url);
      // }

      // const res = await xhr.post<PromoCreateRes>(promoApi, promoData, promoConfig);
      const videoId = await createVideo({ data, additionalDataToPost, config });
      // if (!res.data) throw res;

      // const formData = new FormData();
      //
      // if (!data.youtube_url) {
      //   if (data.file) {
      //     formData.append('file', data.file);
      //   }
      //
      //   await instance.post(res.data.upload_url, formData);
      //   const checkRes = await xhr.get<PromoVideo>(`${promoApi}${res.data.id}/check/`);
      //   if (!checkRes.data) throw checkRes;
      //   setPromo(checkRes.data);
      // }
      //
      // if (data.video_type === 'Y') {
      //   const promoPatchRes = await xhr.patch(`${promoApi}/${res.data.id}/`, formData);
      //   if (!promoPatchRes.data) throw promoPatchRes;
      //   setPromo({ ...promoPatchRes.data });
      // }

      // setUploadStatus('idle');
      // localStorage.setItem('promoId', res.data.id.toString());
      onSuccess && videoId && onSuccess(videoId);
    } catch (error) {
      console.error(error);
    }
  };

  const update = async (data: PromoFormValues) => {
    // setUploadStatus('loading');
    resetPromo();
    try {
      const additionalDataToPost = new FormData();

      if (data.screenshot_url) {
        if (typeof data.screenshot_url === 'string' && data.screenshot_url.includes('base64')) {
          additionalDataToPost.append('preview', base64ToFile(data.screenshot_url));
        } else if (data.screenshot_url instanceof File) {
          additionalDataToPost.append('preview', data.screenshot_url);
        }
      }
      // console.log("DATA BEFORE SEND TO BACK:", data);
      // console.log("additionalDataToPost BEFORE SEND TO BACK:", additionalDataToPost);
      const videoId = await createVideo({ data, additionalDataToPost });
      // const res = await xhr.patch(`${promoApi}${videoId}/`, additionalDataToPost);
      // if (!res.data) throw res;
      //
      // const formData = new FormData();
      //
      // if (!data.youtube_url) {
      //   if (data.file) {
      //     formData.append('file', data.file);
      //   }

      // await instance.post(res.data.upload_url, formData);
      //   const checkRes = await xhr.get<PromoVideo>(`${promoApi}${res.data.id}/check/`);
      //   if (!checkRes.data) throw checkRes;
      //   setPromo(checkRes.data);
      // }
      //
      // if (data.video_type === 'Y') {
      //   const promoPatchRes = await xhr.patch(`${promoApi}/${res.data.id}/`, formData);
      //   if (!promoPatchRes.data) throw promoPatchRes;
      //   setPromo({ ...promoPatchRes.data });
      // }

      // setUploadStatus('idle');
      // localStorage.setItem('promoId', res.data.id.toString());
      onSuccess && videoId && onSuccess(videoId);
    } catch (error) {
      console.error(error);
    }
  };

  const onSubmit = (data: PromoFormValues) => {
    console.log('lol');
    isNaN(videoId) ? create(data) : update(data);
  };
  return {
    onSubmit,
    progress,
    uploadStatus,
  };
};
